import { displayToast, sendAppsignalError } from "../modules/utils";
import AjaxModal from "./ajax_modal";

export default class Recorder {
  constructor(element) {
    this.element = element;

    this.trigger = this.element.querySelector(".js-recorder-trigger");
    this.cancelTrigger = this.element.querySelector(".js-recorder-cancel-trigger");
    this.videoTarget = this.element.querySelector(".js-recorder-video");
    this.inputTarget = this.element.querySelector(".js-recorder-input");
    this.recordText = this.element.querySelector(".js-recorder-text");
    this.iconTarget = this.element.querySelector(".js-recorder-icon");
    this.recorderWrapperTarget = this.element.querySelector(".js-recorder-wrapper");
    this.modalElementUrl = this.element.dataset.modalUrl;

    this.recordingValue = false;
    this.resetRecording = false;
    this.mimeType = this.determineMimeType();

    this.initialize();
  }

  initialize() {
    if (!this.mimeType) return; // No point in initializing if the browser doesn't support the mp4 or webm

    this.trigger.addEventListener("click", () => {
      this.recordingValue ? this.stopRecording() : this.askForVideoAndAudioPermissions();
    });

    this.cancelTrigger.addEventListener("click", () => {
      this.resetRecording = true;
      this.stopRecording();
      this.clearVideoElement();
    });
  }

  determineMimeType() {
    if (MediaRecorder.isTypeSupported("video/webm")) {
      this.extensionType = "webm";
      return "video/webm";
    }

    if (MediaRecorder.isTypeSupported("video/mp4")) {
      this.extensionType = "mp4";
      return "video/mp4";
    }

    displayToast(this.element.dataset.errorMessage);
    sendAppsignalError(`${navigator.userAgent} does not support mp4 & webm`, "Recorder", {});
    return null;
  }

  clearVideoElement() {
    this.videoTarget.src = "";
    this.videoTarget.controls = false;
    this.videoTarget.autoplay = true;
    this.videoTarget.muted = true;
    this.inputTarget.value = "";
  }

  askForVideoAndAudioPermissions() {
    // First we ask for permissiosn to list all possible devices.

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then(() => {
        this.getVideoInputDevices();
      })
      .catch(() => {
        // When the permissions are denied.
        displayToast(this.element.dataset.noPermissions);
      });
  }

  getVideoInputDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) {
      displayToast(this.element.dataset.errorMessage);
      sendAppsignalError("MediaDevices API not supported", "Recorder", {});
      return;
    }

    navigator.mediaDevices.enumerateDevices().then((devices) => {
      this.videoInputDevices = devices.filter((device) => device.kind === "videoinput");

      if (this.videoInputDevices.length === 0) {
        displayToast(this.element.dataset.noMedia);
        sendAppsignalError("No video input devices found", "Recorder", {});
        return;
      }

      if (this.videoInputDevices.length === 1) {
        this.startRecording();
        return;
      }

      this.showDevicesModal(); // When there are multiple devices, show the modal
    });
  }

  showDevicesModal() {
    const modalLink = document.body.appendChild(document.createElement("a"));
    modalLink.href = this.modalElementUrl;
    this.devicesModal = new AjaxModal(modalLink);
    modalLink.remove();

    window.addEventListener(
      "modal:loaded",
      () => {
        if (this.devicesModal.modalElement === undefined) {
          return;
        }

        this.videoSourceSelect = this.devicesModal.modalElement.querySelector(".js-recorder-modal-select");
        this.videoTrigger = this.devicesModal.modalElement.querySelector(".js-recorder-modal-trigger");

        this.videoInputDevices.forEach((device) => {
          const option = document.createElement("option");
          option.value = device.deviceId;
          option.textContent = device.label || `Camera ${this.videoSourceSelect.options.length + 1}`;
          this.videoSourceSelect.appendChild(option);
        });

        this.videoTrigger.addEventListener("click", () => {
          this.startRecording();
        });
      },
      { once: true }
    );
  }

  startRecording() {
    this.setupCameraStream()
      .then(() => this.record(this.originalStream)) // Uses the original stream
      .then((recordedBlob) => this.handleRecordingSuccess(recordedBlob))
      .catch((error) => this.handleRecordingError(error));
  }

  setupCameraStream() {
    this.recorderWrapperTarget.classList.add("is-loading");

    this.trigger.disabled = true;
    this.recordingValue = true;

    this.clearVideoElement();

    const deviceId = this.videoSourceSelect?.value || undefined;

    // We stop any running media tracks to ensure a clean start
    navigator.mediaDevices
      .getUserMedia({ video: { width: 1280, height: 720, deviceId: { exact: deviceId } }, audio: true })
      .then((stream) => stream.getTracks().forEach((track) => track.stop()));

    return navigator.mediaDevices
      .getUserMedia({
        video: { width: 1280, height: 720, deviceId: { exact: deviceId } },
        audio: true,
      })
      .then((stream) => {
        this.videoTarget.srcObject = stream;
        this.originalStream = stream; // Store original stream with video + audio

        this.element.classList.add("is-recording");
        this.recorderWrapperTarget.classList.remove("is-loading");

        this.trigger.disabled = false;
        this.recordText.textContent = this.element.dataset.stopRecordingText;

        return new Promise((resolve) => (this.videoTarget.onplaying = resolve));
      });
  }

  drawFrame() {
    if (!this.video.paused && !this.video.ended) {
      this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
      requestAnimationFrame(() => this.drawFrame());
    }
  }

  record(stream) {
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
    this.video = document.createElement("video");
    this.video.muted = true;

    this.video.srcObject = stream;
    this.video.play();

    this.canvas.width = this.video.videoWidth || 1280;
    this.canvas.height = this.video.videoHeight || 720;

    this.drawFrame();

    const canvasStream = this.canvas.captureStream();
    const audioTracks = stream.getAudioTracks();

    const combinedStream = new MediaStream([...canvasStream.getVideoTracks(), ...audioTracks]);

    this.recorder = new MediaRecorder(combinedStream, {
      mimeType: this.mimeType,
      videoBitsPerSecond: 5000000,
    });

    const data = [];

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        data.push(event.data);
      }
    };

    return new Promise((resolve) => {
      this.recorder.onstop = () => resolve(new Blob(data, { type: this.mimeType }));
      this.recorder.start();
    });
  }

  handleRecordingSuccess(recordedBlob) {
    this.element.classList.remove("is-recording");

    if (this.resetRecording) {
      this.resetRecording = false;
      this.recordingValue = false;

      this.originalStream.getTracks().forEach((track) => track.stop());
      this.originalStream = null;

      return;
    }

    this.videoTarget.srcObject = null;
    this.videoTarget.src = URL.createObjectURL(recordedBlob);
    this.videoTarget.controls = true;

    this.videoTarget.onloadedmetadata = () => {
      this.videoTarget.play();
    };

    const file = new File([recordedBlob], `recording.${this.extensionType}`, { type: this.mimeType });
    const container = new DataTransfer();
    container.items.add(file);

    this.inputTarget.files = container.files;
    console.log(`Successfully recorded ${recordedBlob.size} bytes of ${recordedBlob.type} media.`);

    this.recordingValue = false;
  }

  handleRecordingError(error) {
    const { name } = error;

    const errorMessages = {
      NotReadableError: this.element.dataset.cameraInUse,
      NotAllowedError: this.element.dataset.noPermissions,
      NotFoundError: this.element.dataset.noMedia,
      OverconstrainedError: this.element.dataset.overconstrainedError,
    };

    console.error(error);
    displayToast(errorMessages[name] || this.element.dataset.errorMessage);
    sendAppsignalError(error, "Recorder", {});

    this.recordingValue = false;
    this.trigger.disabled = false;
    this.recorderWrapperTarget.classList.remove("is-loading");
  }

  stopRecording() {
    if (this.originalStream) {
      this.recorder.stop();
    }

    this.videoTarget.srcObject = null;
    this.videoTarget.controls = true;
    this.videoTarget.autoplay = false;
    this.videoTarget.muted = false;

    this.recordText.textContent = this.element.dataset.retryRecordingText;
  }
}
